import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { errMsg } from "../lib/api";

const money = (v) => (v == null ? "unknown" : `$${Number(v).toFixed(4)}`);

export default function ResearchApproval({ title, submit, onClose, onStarted }) {
  const [paid, setPaid] = useState(false);
  const [est, setEst] = useState(null);
  const [unpriced, setUnpriced] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEst(null);
    submit({ paid, approved: false }).then((d) => setEst(d.estimate)).catch((e) => toast.error(errMsg(e)));
  }, [paid]); // eslint-disable-line

  const approve = async () => {
    setBusy(true);
    try {
      const d = await submit({ paid, approved: true, approve_unpriced: unpriced });
      toast.success("Research approved and started");
      onStarted(d);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const needUnpriced = est?.unpriced_items?.length > 0;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="research-approval-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <label className="flex items-center justify-between rounded-md border p-3 text-sm">
          <span><b>Include paid enrichment</b><br /><span className="text-xs text-slate-500">Hunter / Apollo / email verification. Off = public web research only.</span></span>
          <Switch checked={paid} onCheckedChange={setPaid} data-testid="approval-paid-switch" />
        </label>
        {!est ? <div className="text-sm text-slate-500">Calculating estimate…</div> : (
          <div className="space-y-3 text-sm" data-testid="approval-estimate">
            <div><b>{est.restaurants}</b> restaurant(s) · providers used:<ul className="list-disc pl-5 text-xs">{est.providers.map((p) => <li key={p}>{p}</li>)}</ul></div>
            <table className="w-full text-xs"><tbody>{est.lines.map((l) => <tr key={l.item} className="border-t"><td className="py-1">{l.item}</td><td>{l.units.toLocaleString()}</td><td className="text-right">{money(l.est_cost)}</td></tr>)}</tbody></table>
            <div className="flex justify-between font-semibold"><span>Known estimated total</span><span data-testid="approval-total">{money(est.known_total)}{needUnpriced ? " + unknown" : ""}</span></div>
            <div className="flex justify-between text-xs" data-testid="approval-budget"><span>Monthly paid budget remaining</span><span>{money(est.monthly_remaining)} of {money(est.monthly_budget)}</span></div>
            {est.notes.map((n) => <p key={n} className="text-xs text-slate-500">{n}</p>)}
            <p className="rounded bg-slate-50 p-2 text-[11px] text-slate-600">{est.qualification_stage_paid_usage}</p>
            {needUnpriced && <label className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-2 text-xs"><Checkbox checked={unpriced} onCheckedChange={(v) => setUnpriced(!!v)} data-testid="approval-unpriced-checkbox" />I approve usage whose cost cannot be estimated ({est.unpriced_items.join(", ")}).</label>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="approval-cancel-btn">Cancel</Button>
          <Button className="bg-orange-600 hover:bg-orange-700" disabled={!est || busy || (needUnpriced && !unpriced)} onClick={approve} data-testid="approval-confirm-btn">Approve & start research</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
